import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Download, Share } from "lucide-react";

// Sprint 29 — "Add to Home Screen" prompt (PWA).
// Shows after the 2nd visit unless the user is already in standalone mode
// or previously dismissed / installed the app.
const VISITS_KEY = "stellar.pwa.visits";
const DISMISSED_KEY = "stellar.pwa.installBannerDismissed";
const MIN_VISITS = 2;

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // Already installed — do nothing.
    if (localStorage.getItem(DISMISSED_KEY) === "1") return;

    // Count visits (per browser).
    const n = Number(localStorage.getItem(VISITS_KEY) || "0") + 1;
    localStorage.setItem(VISITS_KEY, String(n));

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (n >= MIN_VISITS) setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISSED_KEY, "1");
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt — show manual instructions instead.
    if (isIOS() && n >= MIN_VISITS) {
      setIos(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, "1");
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
    if (outcome === "accepted") localStorage.setItem(DISMISSED_KEY, "1");
  };

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100vw-2rem)] max-w-md rounded-xl border border-border/60 bg-card/95 backdrop-blur shadow-2xl p-4 flex items-start gap-3"
      role="dialog"
      aria-label="App zum Startbildschirm hinzufügen"
    >
      <div className="shrink-0 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
        <Download className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">App installieren</div>
        {ios ? (
          <p className="text-xs text-muted-foreground mt-1">
            Tippen Sie unten in Safari auf <Share className="inline h-3 w-3 mx-0.5" /> „Teilen" und anschließend auf „Zum Home-Bildschirm".
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-1">
            Fügen Sie Stellar Aufbereitung zum Startbildschirm hinzu — schnellerer Zugriff und Vollbildansicht.
          </p>
        )}
        {!ios && (
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={install} data-testid="pwa-install-btn">
              Jetzt installieren
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} data-testid="pwa-install-dismiss">
              Später
            </Button>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 text-muted-foreground hover:text-foreground p-1 -mt-1 -mr-1"
        aria-label="Schließen"
        data-testid="pwa-install-close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
